import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Loader2, AlertTriangle, Pill, Beaker } from "lucide-react";
import SeoHead from "../components/SeoHead";
import { api } from "../lib/api";

/**
 * /categories/:slug — Ingredient-category landing page.
 *
 * Hardcoded curated copy (hero / tagline / intro) merges with the
 * DB-driven list of ingredients in that category + flagged medications
 * + popular products. Renders inside the cream/champagne aesthetic
 * shared by /about, /methodology and /pricing.
 */
const RISK_BADGE = {
  HIGH: { label: "Avoid", cls: "bg-red-100 text-red-800 ring-red-200" },
  MODERATE: { label: "Caution", cls: "bg-amber-100 text-amber-800 ring-amber-200" },
};

export default function CategoryDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api
      .get(`/seo/categories/${encodeURIComponent(slug)}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e.message || "Failed to load category.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <main className="bg-cream-50 min-h-[80vh] flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-ink-mute" />
      </main>
    );
  }

  if (error || !data) {
    return (
      <main
        data-testid="category-detail-error"
        className="bg-cream-50 min-h-[80vh] flex flex-col items-center justify-center px-5 text-center"
      >
        <AlertTriangle className="w-6 h-6 text-amber-700" />
        <h1 className="mt-4 text-[20px] font-semibold text-ink">
          Category not found
        </h1>
        <p className="mt-2 text-[13.5px] text-ink-soft max-w-sm">
          {error || "We couldn't find that category. Pick another from the index."}
        </p>
        <Link
          to="/categories"
          className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-ink text-cream-50 text-[14px] font-bold transition-all hover:scale-[1.01]"
        >
          Browse all categories
          <ArrowRight className="w-4 h-4" />
        </Link>
      </main>
    );
  }

  return (
    <main
      data-testid="category-detail-page"
      className="bg-cream-50 min-h-[80vh]"
    >
      <SeoHead
        title={data.meta?.title || `${data.label} — BlendSafe`}
        description={data.meta?.description || data.tagline}
        canonicalPath={`/categories/${data.slug}`}
      />

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <Link
          to="/categories"
          data-testid="category-back-link"
          className="inline-flex items-center gap-1.5 text-[12px] text-ink-mute hover:text-ink"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All categories
        </Link>

        <header className="mt-4">
          <p className="text-[10.5px] uppercase tracking-[0.28em] text-ink-mute font-semibold">
            {data.label}
          </p>
          <h1
            data-testid="category-headline"
            className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-[-0.025em] text-ink mt-2.5 leading-[1.08]"
          >
            {data.hero}
          </h1>
          <p className="text-[14.5px] sm:text-[15.5px] text-ink-soft/85 mt-5 leading-relaxed max-w-xl">
            {data.tagline}
          </p>
        </header>

        <section
          data-testid="category-intro"
          className="mt-10 rounded-2xl bg-white border border-surface-line p-5 sm:p-6"
        >
          <p className="text-[14px] sm:text-[14.5px] text-ink-soft leading-relaxed">
            {data.intro}
          </p>
        </section>

        {/* Ingredients grid */}
        {data.ingredients?.length > 0 && (
          <section
            data-testid="category-ingredients"
            className="mt-10"
          >
            <h2 className="text-[11px] uppercase tracking-[0.28em] font-bold text-ink-mute flex items-center gap-2">
              <Beaker className="w-3.5 h-3.5" /> Ingredients in this category
            </h2>
            <div className="mt-4 grid sm:grid-cols-2 gap-2.5">
              {data.ingredients.map((ing) => (
                <Link
                  key={ing.id || ing.slug}
                  to={`/ingredients/${ing.slug}`}
                  data-testid={`category-ingredient-${ing.slug}`}
                  className="group flex items-start justify-between gap-3 rounded-xl bg-white border border-surface-line p-4 hover:border-ink-mute transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-[14px] font-semibold text-ink truncate">{ing.name}</p>
                    {ing.description && (
                      <p className="mt-1 text-[12.5px] text-ink-soft leading-snug line-clamp-2">
                        {ing.description}
                      </p>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-ink-light shrink-0 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Flagged medications */}
        {data.medications?.length > 0 && (
          <section
            data-testid="category-medications"
            className="mt-10"
          >
            <h2 className="text-[11px] uppercase tracking-[0.28em] font-bold text-ink-mute flex items-center gap-2">
              <Pill className="w-3.5 h-3.5" /> Medications to watch
            </h2>
            <p className="mt-1.5 text-[13px] text-ink-soft">
              Prescriptions that interact with at least one ingredient in this category.
            </p>
            <ul className="mt-4 divide-y divide-surface-line rounded-2xl bg-white border border-surface-line overflow-hidden">
              {data.medications.map((m) => {
                const badge = RISK_BADGE[m.risk_level] || RISK_BADGE.MODERATE;
                return (
                  <li key={m.id}>
                    <Link
                      to={`/medications/${m.slug}`}
                      data-testid={`category-medication-${m.slug}`}
                      className="flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-cream-50 transition-colors"
                    >
                      <span className="text-[14px] font-semibold text-ink truncate">
                        {m.name}
                      </span>
                      <span className="flex items-center gap-2 shrink-0">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wider ring-1 ${badge.cls}`}
                        >
                          {badge.label}
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 text-ink-light" />
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* CTA */}
        <section
          data-testid="category-cta"
          className="mt-14 rounded-2xl bg-white border border-surface-line p-6 sm:p-8"
        >
          <h2 className="text-[18px] sm:text-[20px] font-semibold text-ink">
            Check your routine against your meds.
          </h2>
          <p className="mt-2 text-[13.5px] text-ink-soft">
            BlendSafe checks {data.ingredient_count}+ {data.label.toLowerCase()} ingredients against your medication list in seconds.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link
              to="/check"
              data-testid="category-cta-check"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-ink hover:bg-ink-soft text-cream-50 text-[14px] font-bold transition-all hover:scale-[1.01] active:scale-[0.99]"
            >
              Check your routine
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/methodology"
              data-testid="category-cta-methodology"
              className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full border border-surface-line bg-cream-50 text-ink text-[14px] font-semibold transition-all hover:bg-cream-100"
            >
              How we score safety
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}
